import moment from 'moment-mini'
import { combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { productSelectors } from 'src/app/features/product/product.selectors'
import { convertFileToBase64, getFullName } from 'src/app/shared/utils'

import { Component, ElementRef, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Store } from '@ngrx/store'
import { TranslateService } from '@ngx-translate/core'
import { saveAs } from 'file-saver'
import {
  EmployeeBooking,
  EmployeeBookingActionTypes
} from '../../employee-booking/employee-booking'
import { employeeBookingActions } from '../../employee-booking/employee-booking.actions'
import { employeeBookingSelectors } from '../../employee-booking/employee-booking.selectors'
import { EmployeeBookingService } from '../../employee-booking/employee-booking.service'
import { employeeContractManagerLinkSelectors } from '../../employee-contract-manager-link/employee-contract-manager-link.selectors'
import { EmployeeContract } from '../../employee-contract/employee-contract'
import { employeeContractSelectors } from '../../employee-contract/employee-contract.selectors'
import { EmployeeContractService } from '../../employee-contract/employee-contract.service'
import { Actions, ofType } from '@ngrx/effects'
import { EmployeeContractManagerLink } from '../../employee-contract-manager-link/employee-contract-manager-link'
import { Dictionary } from '@ngrx/entity'
import { RegisterUploadedDocumentsService } from 'src/app/shared/services/register-uploaded-documents.service'
import { ConfigOptionsKeys } from 'src/app/shared/constants'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { environment } from 'src/environments/environment'
import { timeBlockActions } from 'src/app/features/time-block/time-block.actions'
import { TimeBlockType } from 'src/app/features/time-block/time-block'
import { HttpClient } from '@angular/common/http'
import { MemberService } from 'src/app/features/member/member.service'

@UntilDestroy()
@Component({
  selector: 'supervisor-booking-index',
  templateUrl: './index.component.html',
  styleUrls: ['./index.component.scss']
})
export class EmployeeBookingInvoiceComponent implements OnInit {
  public contracts$: Observable<EmployeeContract[]>
  public member$: Observable<Member>
  public selectedContract$: Observable<EmployeeContract>
  public form: UntypedFormGroup
  public formInvoiceDetails: UntypedFormGroup
  public contractEmployeeBookingsToBeInvoiced$: Observable<EmployeeBooking[]>
  public employeeBookingActionTypes = EmployeeBookingActionTypes
  public employeeContractManagerLinkEntities$: Observable<
    Dictionary<EmployeeContractManagerLink>
  >
  public showError: boolean = false
  public isLoading$: Observable<boolean>
  public debtorText: string = 'your finance department'
  private productsIds = []
  private selectedContract: EmployeeContract
  private employeeBookings: EmployeeBooking[]
  private selectedContractId: number
  public uploadedFiles: File[]
  public isUploadedFilesValid: boolean = true
  public showSendInvoiceButton: boolean = environment.showSendInvoiceButton
  public sendingInProgress: boolean = false
  public attachDocumentToInvoiceConfig$: Observable<{
    isVisible: boolean
    isRequired: boolean
  }>
  public documents: any[]

  constructor(
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef,
    private store: Store<AppState>,
    public employeeBookingService: EmployeeBookingService,
    private notificationService: NotificationService,
    private translate: TranslateService,
    private employeeContractService: EmployeeContractService,
    private actions$: Actions,
    private registerUploadDocumentService: RegisterUploadedDocumentsService,
    private http: HttpClient,
    private memberService: MemberService
  ) {}

  ngOnInit() {
    this.uploadedFiles = []
    this.registerUploadDocumentService.resetUploadedFiles()

    this.attachDocumentToInvoiceConfig$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.ATTACH_DOCUMENTS_TO_INVOICE
      )
      .pipe(
        filter((configurationOption) => Boolean(configurationOption?.value)),
        map((configurationOption) => {
          return JSON.parse(configurationOption.value)
        })
      )

    this.form = this.fb.group({
      contractId: [null, []]
    })

    this.formInvoiceDetails = this.fb.group({
      invoiceNumber: [null, [Validators.required]],
      expirationDate: [moment().add(30, 'days').toDate(), [Validators.required]]
    })

    this.isLoading$ = this.store.select(
      employeeBookingSelectors.selectIsLoading
    )
    this.member$ = this.store.select(authSelectors.selectMember)

    this.member$
      .pipe(
        filter((x) => Boolean(x)),
        take(1)
      )
      .subscribe((member) => {
        this.memberService
          .getEmployeeDocuments(member.id)
          .pipe(take(1))
          .subscribe((response: any) => {
            this.documents = response.data ?? []
          })
      })

    this.employeeContractManagerLinkEntities$ = this.store.select(
      employeeContractManagerLinkSelectors.selectEntities
    )

    this.contracts$ = this.employeeContractManagerLinkEntities$.pipe(
      withLatestFrom(
        this.member$,
        this.store.select(employeeContractSelectors.selectAll)
      ),
      map(
        ([employeeContractManagerLinkEntities, member, employeeContracts]) => {
          const filteredContracts = employeeContracts.filter(
            (employeeContract) => {
              if (!employeeContract.debtorPersonId) {
                return false
              }
              return true
            }
          )

          const filteredAvailableContracts =
            this.employeeContractService.getAvailableContracts(
              filteredContracts,
              employeeContractManagerLinkEntities,
              member
            )

          if (filteredAvailableContracts?.length == 1) {
            this.form.controls.contractId.setValue(
              filteredAvailableContracts[0].id
            )
          }

          return filteredAvailableContracts
        }
      )
    )

    this.selectedContract$ = this.form.controls.contractId.valueChanges.pipe(
      filter((contractId) => Boolean(contractId)),
      switchMap((contractId) =>
        this.store.select(employeeContractSelectors.selectById, contractId)
      )
    )

    this.contractEmployeeBookingsToBeInvoiced$ = this.selectedContract$.pipe(
      tap((contract) => {
        if (this.selectedContractId == contract.id) return
        this.selectedContractId = contract.id
        this.selectedContract = contract
        this.store.dispatch(
          employeeBookingActions.fetchMany({
            params: RequestQueryBuilder.create({
              search: {
                contractId: contract.id
              },
              join: [{ field: 'product' }]
            })
          })
        )
        this.actions$
          .pipe(ofType(employeeBookingActions.upsertMany), take(1))
          .subscribe((action) => {
            if (!action) {
              return
            }
            let particularProductIds = []
            for (const employeeB of action.entities) {
              if (
                employeeB.productId &&
                !this.productsIds.includes(employeeB.productId)
              ) {
                this.productsIds.push(employeeB.productId)
                particularProductIds.push(employeeB.productId)
              }
            }
            if (particularProductIds.length > 0) {
              particularProductIds.forEach((productId) => {
                setTimeout(() => {
                  this.store.dispatch(
                    timeBlockActions.fetchManyByItemTypeAndId({
                      itemId: productId,
                      timeBlockType: TimeBlockType.PRODUCT
                    })
                  )
                }, 300)
              })
            }
          })
      }),
      switchMap((contract) =>
        this.store.select(employeeBookingSelectors.selectByFilter, {
          field: 'contractId',
          value: contract.id
        })
      ),
      withLatestFrom(this.employeeContractManagerLinkEntities$),
      map(([employeeBookings, employeeContractManagerLinkEntities]) => {
        const agendaCloseDate = this.employeeBookingService.getAgendaCloseDate(
          this.selectedContract,
          employeeContractManagerLinkEntities
        )

        return employeeBookings.filter((employeeBooking) => {
          if (!agendaCloseDate) {
            return true
          }
          return (
            !employeeBooking.invoiceDate &&
            moment(employeeBooking.endDate).isBefore(moment(agendaCloseDate))
          )
        })
      }),
      withLatestFrom(
        this.selectedContract$,
        this.store.select(productSelectors.selectEntities)
      ),
      map(([bookings, contract, productEntities]) => {
        const sortedBookings = bookings.sort((a, b) =>
          moment(b.startDate).valueOf() < moment(a.startDate).valueOf() ? -1 : 1
        )

        const bookingsExtended =
          this.employeeBookingService.calculateInvoiceData(
            sortedBookings,
            contract,
            productEntities
          )
        this.employeeBookings = bookingsExtended
        this.showError = bookingsExtended.length == 0

        return bookingsExtended
      })
    )

    combineLatest([
      this.selectedContract$,
      this.store.select(memberSelectors.selectEntities),
      this.translate.get('employeeModule.financeDepartment')
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([contract, memberEntities, financeDepartmentTranslation]) => {
        if (contract?.debtorPersonId) {
          const debtor = memberEntities[contract.debtorPersonId]
          this.debtorText = getFullName(debtor)
          return
        }

        this.debtorText = financeDepartmentTranslation.toLowerCase()
      })
  }

  public onClickSendInvoice() {
    if (!this.selectedContract?.checkedTillDate) {
      return this.notificationService.emit(
        'alerts.calendarNeedsToBeChecked',
        'warning'
      )
    }

    this.setIsUploadedFilesValid()
    this.attachDocumentToInvoiceConfig$.pipe(take(1)).subscribe((configs) => {
      if (
        configs.isVisible &&
        configs.isRequired &&
        !this.isUploadedFilesValid
      ) {
        this.notificationService.emit(
          'employeeModule.needToAttachDocument',
          'warning'
        )
        return
      }
    })

    this.employeeContractManagerLinkEntities$
      .pipe(take(1))
      .subscribe((employeeContractManagerLinkEntities) => {
        const managerLinksOfContract =
          this.selectedContract?.employeeContractManagerLinks.map(
            (managerLinkId) =>
              employeeContractManagerLinkEntities?.[managerLinkId]
          )

        const managerHasToCheckContract: boolean = Boolean(
          managerLinksOfContract?.find(
            (managerLink) =>
              !managerLink.checkedTillDate ||
              moment(managerLink.checkedTillDate).isBefore(
                moment(
                  this.employeeBookings[this.employeeBookings.length - 1]
                    ?.startDate
                )
              )
          )
        )

        // if (managerHasToCheckContract) {
        //   return this.notificationService.emit(
        //     'alerts.calendarIsNotCheckedByAllManager',
        //     'warning'
        //   )
        // }

        this.sendInvoice()
      })
  }

  public onClickDownloadInvoiceSummary() {
    this.store.dispatch(employeeBookingActions.setLoading({ isLoading: false }))

    this.member$.pipe(take(1)).subscribe((member) => {
      return this.http
        .post(
          `${environment.backendApiUrl}/employee-bookings/actions`,
          {
            actionType: EmployeeBookingActionTypes.DOWNLOAD_INVOICE_HISTORY,
            personId: member.id,
            contractId: Number(this.form.value.contractId),
            params: {}
          },
          { responseType: 'arraybuffer' }
        )
        .pipe(
          take(1),
          catchError((e: Error) => {
            this.store.dispatch(
              employeeBookingActions.actionFinished({
                actionFinished: {
                  action: employeeBookingActions.runAction.type,
                  data: []
                }
              })
            )
            return of(null)
          })
        )
        .subscribe(
          (response) => {
            const blob = new Blob([response], { type: 'application/pdf' })
            saveAs(blob, `invoice-${this.form.value.contractId}.pdf`)

            this.store.dispatch(
              employeeBookingActions.actionFinished({
                actionFinished: {
                  action: employeeBookingActions.runAction.type,
                  data: []
                }
              })
            )
          },
          (error) => {
            this.store.dispatch(
              employeeBookingActions.actionFinished({
                actionFinished: {
                  action: employeeBookingActions.runAction.type,
                  data: []
                }
              })
            )
            console.error('Error downloading the PDF', error)
          }
        )
    })
  }

  private async sendInvoice() {
    this.sendingInProgress = true

    const attachment = {
      name: null,
      base64Content: null
    }
    if (this.uploadedFiles[0]) {
      attachment.name = this.uploadedFiles[0].name
      attachment.base64Content = await convertFileToBase64(
        this.uploadedFiles[0]
      )
    }

    const invoiceNumber = this.formInvoiceDetails.value.invoiceNumber
    const expirationDate = moment(this.formInvoiceDetails.value.expirationDate)
      .startOf('day')
      .toISOString()

    this.member$
      .pipe(take(1), withLatestFrom(this.translate.get('invoiceText')))
      .subscribe(([member, translations]) => {
        this.store.dispatch(
          employeeBookingActions.runAction({
            actionType: EmployeeBookingActionTypes.SEND_INVOICE,
            personId: member.id,
            contractId: Number(this.form.value.contractId),
            params: {
              attachment,
              invoiceNumber,
              expirationDate
            },
            invoiceText: {
              factuur: translations.factuur,
              factuurnummer: translations.factuurnummer,
              factuurdatum: translations.factuurdatum,
              nummer: translations.nummer,
              referentie: translations.referentie,
              totaal: translations.totaal,
              omschrijving: translations.omschrijving,
              uren: translations.uren,
              uurtarief: translations.uurtarief,
              subtotaal: translations.subtotaal,
              footerText: translations.footerText,
              vervaldatum: translations.vervaldatum,
              constractNumber: translations.contractNumber,
              ongedateerd: translations.ongedateerd,
              zonderUren: translations.zonderUren,
              referentienummer: translations.referentienummer
            }
          })
        )

        this.actions$
          .pipe(
            ofType(employeeBookingActions.actionFinished),
            take(1),
            timeout(5000),
            catchError((error) => {
              this.notificationService.emit('alerts.error', 'warning')
              this.sendingInProgress = false
              return of(null)
            })
          )
          .subscribe((action) => {
            this.sendingInProgress = false

            if (!action) {
              this.notificationService.emit('alerts.error', 'warning')
              return
            }
            this.store.dispatch(
              employeeBookingActions.fetchMany({
                params: RequestQueryBuilder.create({
                  search: {
                    contractId: this.selectedContract.id
                  },
                  join: [{ field: 'product' }]
                })
              })
            )
            this.notificationService.emit(
              'employeeModule.invoiceSent',
              'success'
            )
          })
      })
  }

  private setIsUploadedFilesValid() {
    this.attachDocumentToInvoiceConfig$.pipe(take(1)).subscribe((configs) => {
      if (!configs.isVisible || !configs.isRequired) {
        this.isUploadedFilesValid = true
        return
      }

      this.isUploadedFilesValid = this.uploadedFiles?.length > 0
    })
  }

  public removeUploadedFile(file: File) {
    this.uploadedFiles = this.uploadedFiles.filter((f) => f.name != file.name)
    this.setIsUploadedFilesValid()
    this.registerUploadDocumentService.removeUploadedFile(file)
  }

  public onSubmitDocument(file: File) {
    if (Boolean(this.uploadedFiles.find((f) => f.name == file.name))) {
      this.notificationService.emit('general.fileAlreadyUploaded', 'warning')
      return
    }

    this.uploadedFiles.push(file)
    this.setIsUploadedFilesValid()
    this.registerUploadDocumentService.addUploadedFile(file)
  }

  getTodayDateForInput(): string {
    return moment().format('YYYY-MM-DD')
  }

  isExpirationDateInvalid(): boolean {
    const expirationDateValue =
      this.formInvoiceDetails.get('expirationDate')?.value
    if (!expirationDateValue) return false

    const expirationDate = new Date(expirationDateValue)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return expirationDate < today
  }
}
